import { Suspense } from 'react';

import { TAccount } from '@helpers/Accounts/accounts.types';
import { IconCheckDefault, IconClockOutline, IconEuro } from '@iwoca/icons';
import { Pill } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { FormattedMessage } from 'react-intl';

import { useGetCreditBuilderData } from './useGetCreditBuilderData';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { InformationSVG } from '../../../../svgComponents/informationSVG/InformationSVG';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';

const CURRENT_PROGRESS = {
    1: 'top-0',
    2: 'top-[32.00%]'
} as const;

export const CreditBuilderProgress = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => (
    <ErrorBoundary fallback={() => <FallbackErrorModal />}>
        <Suspense fallback={<MainContentPending />}>
            <CreditBuilderContent accountId={accountId} />
        </Suspense>
    </ErrorBoundary>
);

const CreditBuilderContent = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => {
    const { activeStep } = useGetCreditBuilderData(accountId);
    return (
        <div className="col-span-5 h-fit pt-xl max-sm:col-span-full max-sm:pt-m">
            <div className="gap-s pb-xl">
                <Text className="font-med">
                    <FormattedMessage
                        id="_.credit_builder_widget.credit_builder_title"
                        defaultMessage="Your credit journey"
                    />
                </Text>
                <Text className="text-s">
                    <FormattedMessage
                        id="_.credit_builder_widget.credit_builder_subtitle"
                        defaultMessage="Improve your credit with regular repayments to demonstrate your financial reliability and graduate toward our prime flexible lending."
                    />
                </Text>
            </div>

            <div className="relative flex gap-xl">
                {/* Vertical gradient timeline bar with step indicators */}
                <div className={`relative w-[16px] shrink-0`}>
                    <div className="absolute inset-0 rounded-full bg-gradient-to-b from-primary-10 to-primary-40" />
                    <StepIndicator stepNumber={1} isActive={activeStep === 1} />
                    <StepIndicator stepNumber={2} isActive={activeStep === 2} />
                </div>

                {/* Steps container */}
                <div className="flex flex-col">
                    <CreditBuilderStep
                        stepNumber={1}
                        isActive={activeStep === 1}
                        isComplete={activeStep > 1}
                        isNextStep={false} //first step will always be false
                        stepLabel={
                            <FormattedMessage
                                id="_.credit_builder_widget.3_month_micro_loan_title"
                                defaultMessage="3 month credit builder loan"
                            />
                        }
                        stepFundingMaxAmount={6_500}
                        stepDurationMaxAmount={3}
                        stepIsTopUpEligible={false}
                    />

                    <CreditBuilderStep
                        stepNumber={2}
                        isActive={activeStep === 2}
                        isComplete={activeStep > 2}
                        isNextStep={activeStep + 1 === 2}
                        stepLabel={
                            <FormattedMessage
                                id="_.credit_builder_widget.6_month_micro_loan_title"
                                defaultMessage="6 month credit builder loan"
                            />
                        }
                        stepFundingMaxAmount={13_000}
                        stepDurationMaxAmount={6}
                        stepIsTopUpEligible={false}
                    />
                    <CreditBuilderStep
                        stepNumber={3}
                        isActive={activeStep === 3}
                        isComplete={activeStep > 3}
                        isNextStep={activeStep + 1 === 3}
                        stepLabel={
                            <FormattedMessage
                                id="_.credit_builder_widget.flexi_loan_title"
                                defaultMessage="12 Month flexi-loan"
                            />
                        }
                        stepFundingMaxAmount={1_000_000}
                        stepDurationMaxAmount={12}
                        stepIsTopUpEligible={true}
                    />
                </div>
            </div>

            <div className="flex items-center gap-xs font-reg">
                <InformationSVG className="shrink-0" />
                <FormattedMessage
                    id="_.credit_builder_widget.credit_builder_info_text"
                    defaultMessage="Building creditworthiness is not always a straightforward process, and your access to extended credit offers depends on timely repayments and a positive current credit assessment of your company."
                />
            </div>
        </div>
    );
};

const StepIndicator = ({
    stepNumber,
    isActive
}: {
    stepNumber: 1 | 2;
    isActive: boolean;
}) => {
    if (!isActive) return null;

    return (
        <div
            className={`absolute left-0 grid h-[20px] w-[16px] place-items-center ${CURRENT_PROGRESS[stepNumber]}`}
            data-testid={`step-indicator-${stepNumber}`}
        >
            <div className={`size-[16px] rounded-full bg-white`} />
        </div>
    );
};

const CreditBuilderStep = ({
    isComplete = false,
    isActive = false,
    isNextStep = false,
    stepLabel,
    stepNumber,
    stepFundingMaxAmount,
    stepDurationMaxAmount,
    stepIsTopUpEligible
}: {
    isComplete: boolean;
    isActive: boolean;
    stepNumber: number;
    stepLabel: React.ReactNode;
    stepFundingMaxAmount: number;
    stepDurationMaxAmount: number;
    stepIsTopUpEligible: boolean;
    isNextStep: boolean;
}) => (
    <div className="pb-2xl" data-testid={`step-${stepNumber}`}>
        {/* Step content */}
        <div className="flex flex-col gap-l">
            {/* Step title with pill */}
            <div className="flex flex-wrap items-center gap-s">
                <Text className="text-m font-med text-primary-10">
                    {stepLabel}
                </Text>
                {isActive && (
                    <Pill size="small" className="bg-success-lt">
                        <Text className="px-s text-xs font-med text-success-drk">
                            <FormattedMessage
                                id="_.credit_builder_widget.current_step"
                                defaultMessage="You're here"
                            />
                        </Text>
                    </Pill>
                )}
                {isNextStep && (
                    <Pill size="small" className="bg-secondary-95">
                        <Text className="px-s text-xs font-med text-secondary-40">
                            <FormattedMessage
                                id="_.credit_builder_widget.next_step"
                                defaultMessage="Next milestone"
                            />
                        </Text>
                    </Pill>
                )}
            </div>

            {/* Features list - vertically stacked */}
            <div className="flex flex-col gap-s">
                <StepFeature
                    icon={<IconEuro className="fill-error text-xs" />}
                    label={
                        <FormattedMessage
                            id="_.credit_builder_widget.funding_max_amount"
                            defaultMessage="Up to {fundingMaxAmount, number, :: currency/GBP}"
                            values={{
                                fundingMaxAmount: stepFundingMaxAmount
                            }}
                        />
                    }
                    isActive={isActive}
                    isComplete={isComplete}
                />
                <StepFeature
                    icon={<IconClockOutline className="fill-error text-xs" />}
                    label={
                        <FormattedMessage
                            id="_.credit_builder_widget.duration_max_amount"
                            defaultMessage="Up to {stepDurationMaxAmount} months"
                            values={{ stepDurationMaxAmount }}
                        />
                    }
                    isActive={isActive}
                    isComplete={isComplete}
                />
                {stepIsTopUpEligible && (
                    <StepFeature
                        icon={
                            <IconCheckDefault className="fill-error text-xs" />
                        }
                        label={
                            <FormattedMessage
                                id="_.credit_builder_widget.top_up"
                                defaultMessage="TopUp in-loan"
                            />
                        }
                        isActive={isActive}
                        isComplete={isComplete}
                    />
                )}
            </div>
        </div>
    </div>
);

const StepFeature = ({
    icon,
    label
}: {
    icon: React.ReactNode;
    label: React.ReactNode;
    isActive: boolean;
    isComplete: boolean;
}) => (
    <div className="flex items-center gap-s">
        <>{icon}</>
        <Text className="text-xs text-primary-10">{label}</Text>
    </div>
);
